import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import TableTwoContainer from "Common/TableTwoContainer";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

type WinnersTableProps = {
  winners?: any[];
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  currentPage: number;
  totalPages: number;
  showSlotColumns?: boolean;
  showActions?: boolean;
};

const formatCurrency = (amount: number | string | null | undefined) =>
  Number(amount || 0).toLocaleString("en-NG", {
    style: "currency",
    currency: "NGN",
  });

const formatDate = (value: unknown) => {
  if (!value) return "—";
  if (Array.isArray(value) && value.length >= 6) {
    const [y, m, d, h, min, s = 0] = value as number[];
    return new Date(y, m - 1, d, h, min, s).toLocaleString();
  }
  return new Date(value as string).toLocaleString();
};

const StatusBadge = ({ status }: { status: unknown }) => {
  const label =
    typeof status === "object" && status !== null && "name" in status
      ? String((status as { name: string }).name)
      : String(status || "UNKNOWN");
  const color =
    label === "WON"
      ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"
      : "bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300";
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${color}`}>
      {label}
    </span>
  );
};

const WinnersTable = ({
  winners = [],
  isLoading,
  isFetching,
  hasNextPage,
  hasPreviousPage,
  goToNextPage,
  goToPreviousPage,
  currentPage,
  totalPages,
  showSlotColumns = true,
  showActions = false,
}: WinnersTableProps) => {
  const columns = useMemo<ColumnDef<any, unknown>[]>(() => {
    const cols: ColumnDef<any, unknown>[] = [
      {
        header: "Winner ID",
        accessorKey: "id",
      },
      {
        header: "MSISDN",
        accessorKey: "msisdn",
        cell: (info) =>
          String(info.getValue() ?? "") || info.row.original.phoneNumber || "—",
      },
      {
        header: "Date Won",
        accessorKey: "dateWon",
        cell: (info) => formatDate(info.getValue()),
      },
      {
        header: "Prize Won",
        accessorKey: "prizeAmount",
        cell: (info) => formatCurrency(info.getValue() as number),
      },
      {
        header: "Rank",
        accessorKey: "ranking",
        cell: (info) => (info.getValue() as number | null | undefined) ?? "—",
      },
      {
        header: "Full Name",
        accessorKey: "fullName",
        cell: (info) => String(info.getValue() ?? "") || "—",
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (info) => <StatusBadge status={info.getValue()} />,
      },
      {
        header: "Game Won",
        accessorKey: "gameTitle",
        cell: (info) => String(info.getValue() ?? "") || "—",
      },
      {
        header: "Ticket #",
        accessorKey: "ticketNumber",
        cell: (info) => String(info.getValue() ?? "") || "—",
      },
    ];

    if (showSlotColumns) {
      cols.push(
        {
          header: "Slot #",
          accessorKey: "slotNumber",
          cell: (info) => (info.getValue() as number | null | undefined) ?? "—",
        },
        {
          header: "Slot Title",
          accessorKey: "slotTitle",
          cell: (info) => String(info.getValue() ?? "") || "—",
        }
      );
    }

    if (showActions) {
      cols.push({
        header: "Actions",
        accessorKey: "id",
        cell: () => "—",
      });
    }

    return cols;
  }, [showSlotColumns, showActions]);

  if (isLoading) {
    return <div className="noresult py-8">Loading winners...</div>;
  }

  if (!winners.length) {
    return (
      <div className="noresult my-5">
        <h6>No winners found for this slot</h6>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <TableTwoContainer columns={columns} data={winners} />
      <div className="mt-4 flex justify-end items-center gap-4">
        <button
          type="button"
          onClick={goToPreviousPage}
          disabled={!hasPreviousPage}
          className="flex items-center px-3 py-1 bg-gray-500 text-white rounded disabled:opacity-50"
        >
          <ArrowLeftIcon className="mr-2 w-4 h-4" />
          {isFetching ? "Loading..." : "Prev"}
        </button>
        <span className="text-base font-medium">
          Page {currentPage + 1} of {totalPages}
        </span>
        <button
          type="button"
          onClick={goToNextPage}
          disabled={!hasNextPage || isFetching}
          className="flex items-center px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
        >
          {isFetching ? "Loading..." : "Next"}
          <ArrowRightIcon className="ml-2 w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default WinnersTable;
